import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Check, ArrowUpRight } from "lucide-react";

import { fadeUp, EASE } from "@/lib/motion";
import { parseError } from "@/lib/errors";
import { trackBusinessLoginClick } from "@/lib/analytics";
import { Logo } from "@/components/landing/Logo";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Same-origin `/api` — Emergent's ingress routes `/api/*` to the backend on
// every frontend host (preview, .emergent.host, custom domain), so no
// `REACT_APP_BACKEND_URL` swap is needed and CORS never applies.
const API = "/api";

const CATEGORY_OPTIONS = [
  "Hair",
  "Makeup",
  "Brows & Lashes",
  "Spray Tans",
  "Barber",
  "Skin & Injectables",
  "Nails",
  "Massage & Body",
  "Wellness",
];

const CITY_OPTIONS = [
  "Sydney",
  "Melbourne",
  "Brisbane",
  "Perth",
  "Adelaide",
  "Gold Coast",
  "Canberra",
  "Hobart",
  "Darwin",
];

export default function BusinessApply() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    business_name: "",
    contact_name: "",
    email: "",
    phone: "",
    city: "",
  });
  const [categories, setCategories] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [errorFields, setErrorFields] = useState(new Set());

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errorFields.has(k)) {
      setErrorFields((prev) => {
        const next = new Set(prev);
        next.delete(k);
        return next;
      });
    }
  };

  const toggleCategory = (cat) => {
    setCategories((cs) => (cs.includes(cat) ? cs.filter((c) => c !== cat) : [...cs, cat]));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    setErrorFields(new Set());
    if (!form.city) {
      setError("Please choose your city.");
      setErrorFields(new Set(["city"]));
      return;
    }
    if (categories.length === 0) {
      setError("Pick at least one service category.");
      setErrorFields(new Set(["categories"]));
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, categories };
      const res = await fetch(`${API}/business-leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        const { message, fields } = parseError(body, "Something went wrong.");
        setErrorFields(fields);
        throw new Error(message);
      }
      // Use the API's normalised response (phone in +61 form) for the
      // register redirect so downstream tooling always gets one shape.
      const saved = await res.json().catch(() => payload);
      const leadForStorage = { ...payload, ...saved };
      try {
        localStorage.setItem("bookaway_business_lead", JSON.stringify(leadForStorage));
        localStorage.setItem("bookaway_pricing_revealed", "1");
      } catch {}
      toast.success("Thanks — here are our plans.");
      navigate("/pricing");
    } catch (err) {
      setError(err.message || "Could not submit — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="business-apply-page" className="relative min-h-screen bg-[#F7F5F2]">
      <div className="grain" />
      <header className="relative z-10 border-b border-black/10 bg-[#F7F5F2]/70 backdrop-blur-xl">
        <div className="mx-auto flex h-20 max-w-[1600px] items-center justify-between px-6 md:px-12">
          <Link to="/" className="flex items-center gap-3">
            <Logo size={44} />
            <span className="font-serif text-2xl font-semibold tracking-tight text-[#2A2724]">
              Book Away
            </span>
          </Link>
        </div>
      </header>

      <section className="relative z-10 px-6 py-16 md:px-12 md:py-24">
        <div className="mx-auto max-w-2xl">
          <motion.p variants={fadeUp} initial="hidden" animate="show" className="overline mb-4">
            For Business
          </motion.p>
          <motion.h1
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.05, duration: 0.7, ease: EASE }}
            className="font-serif text-4xl md:text-5xl font-light leading-[1.05] tracking-tight text-[#2A2724]"
          >
            Tell us about your studio.
          </motion.h1>
          <motion.p
            variants={fadeUp}
            initial="hidden"
            animate="show"
            transition={{ delay: 0.15, duration: 0.7, ease: EASE }}
            className="mt-5 max-w-lg text-[#6B6B6B] leading-relaxed"
          >
            A few quick details and we'll show you the membership options that fit
            your studio best.
          </motion.p>

          <form onSubmit={submit} className="mt-10 space-y-6" data-testid="business-apply-form">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Field label="Business name" invalid={errorFields.has("business_name")}>
                <input
                  data-testid="business-apply-business-name"
                  required
                  value={form.business_name}
                  onChange={set("business_name")}
                  placeholder="Maison Noir"
                  className={inputCls(errorFields.has("business_name"))}
                />
              </Field>
              <Field label="Your name" invalid={errorFields.has("contact_name")}>
                <input
                  data-testid="business-apply-contact-name"
                  required
                  value={form.contact_name}
                  onChange={set("contact_name")}
                  placeholder="Alex Rivera"
                  className={inputCls(errorFields.has("contact_name"))}
                />
              </Field>
              <Field label="Work email" invalid={errorFields.has("email")}>
                <input
                  data-testid="business-apply-email"
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@studio.com"
                  className={inputCls(errorFields.has("email"))}
                />
              </Field>
              <Field label="Phone" invalid={errorFields.has("phone")}>
                <input
                  data-testid="business-apply-phone"
                  required
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="0400 000 000"
                  className={inputCls(errorFields.has("phone"))}
                />
              </Field>
              <Field
                label="City"
                className="sm:col-span-2"
                invalid={errorFields.has("city")}
              >
                <Select
                  value={form.city}
                  onValueChange={(v) => {
                    setForm((f) => ({ ...f, city: v }));
                    if (errorFields.has("city")) {
                      setErrorFields((prev) => {
                        const next = new Set(prev);
                        next.delete("city");
                        return next;
                      });
                    }
                  }}
                >
                  <SelectTrigger
                    data-testid="business-apply-city"
                    className={`h-auto w-full rounded-xl border bg-white/70 px-4 py-3 text-sm text-[#2A2724] focus:ring-0 ${
                      errorFields.has("city")
                        ? "border-[#a13a3a] focus:border-[#a13a3a]"
                        : "border-black/15 focus:border-gold"
                    }`}
                  >
                    <SelectValue placeholder="Choose your city" />
                  </SelectTrigger>
                  <SelectContent>
                    {CITY_OPTIONS.map((c) => (
                      <SelectItem
                        key={c}
                        value={c}
                        data-testid={`business-apply-city-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      >
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>

            <div>
              <label className="mb-2 block text-xs uppercase tracking-[0.18em] text-[#6B6B6B]">
                Service categories · pick all that apply
              </label>
              <div
                data-testid="business-apply-categories"
                className={`flex flex-wrap gap-2 ${
                  errorFields.has("categories")
                    ? "rounded-xl ring-1 ring-[#a13a3a]/50 p-2"
                    : ""
                }`}
              >
                {CATEGORY_OPTIONS.map((cat) => {
                  const active = categories.includes(cat);
                  return (
                    <button
                      key={cat}
                      type="button"
                      data-testid={`business-apply-cat-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => {
                        toggleCategory(cat);
                        if (errorFields.has("categories")) {
                          setErrorFields((prev) => {
                            const next = new Set(prev);
                            next.delete("categories");
                            return next;
                          });
                        }
                      }}
                      aria-pressed={active}
                      className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm transition-colors duration-200 ${
                        active
                          ? "border-transparent bg-[#2A2724] text-white"
                          : "border-black/15 bg-white/60 text-[#2A2724] hover:border-gold"
                      }`}
                    >
                      {active && <Check className="h-3.5 w-3.5" strokeWidth={2.5} />}
                      {cat}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-[0.7rem] uppercase tracking-[0.2em] text-[#9a9a9a]">
                {categories.length === 0
                  ? "None selected yet"
                  : `${categories.length} selected`}
              </p>
            </div>

            {error && (
              <p
                data-testid="business-apply-error"
                className="rounded-2xl border border-[#a13a3a]/30 bg-[#F8ECEC] p-4 text-sm text-[#a13a3a]"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="business-apply-submit"
              disabled={submitting}
              className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-medium text-black transition-colors duration-300 hover:bg-[#AA8627] disabled:opacity-70"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  See pricing
                  <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </>
              )}
            </button>
            <p className="text-center text-[0.7rem] uppercase tracking-[0.22em] text-[#9a9a9a]">
              By continuing you agree to be contacted about Book Away.
            </p>
            <div className="mt-10 text-center">
              <p className="mb-3 text-sm text-[#6B6B6B]">Already have an account?</p>
              <a
                data-testid="business-apply-login-link"
                href={LOGIN_URL}
                onClick={() => trackBusinessLoginClick()}
                className="inline-flex w-full items-center justify-center rounded-full bg-gold px-8 py-4 text-base font-semibold text-[#2A2724] shadow-sm transition-colors duration-300 hover:bg-[#AA8627] sm:w-auto sm:min-w-[260px]"
              >
                Log in
              </a>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

const inputCls = (invalid = false) =>
  `w-full rounded-xl border bg-white/70 px-4 py-3 text-sm text-[#2A2724] placeholder:text-[#9a9a9a] outline-none transition-colors ${
    invalid
      ? "border-[#a13a3a] focus:border-[#a13a3a]"
      : "border-black/15 focus:border-gold"
  }`;

const LOGIN_URL =
  process.env.REACT_APP_LOGIN_URL || "https://app.bookaway.com.au/login";

function Field({ label, children, className = "", invalid = false }) {
  return (
    <label className={`block ${className}`}>
      <span
        className={`mb-1.5 block text-xs uppercase tracking-[0.18em] ${
          invalid ? "text-[#a13a3a]" : "text-[#6B6B6B]"
        }`}
      >
        {label}
      </span>
      {children}
    </label>
  );
}
